import { i18n } from "@/plugins/i18n.ts";

import { AllowBlockScope, TicketAction, TicketTrigger, TicketType } from "@/constants/tickets.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { defineStore } from "pinia";
import { useFiltersStore } from "@/_store/filters.module.ts";
import type { OpenSearchCursor, Paged, Pagination } from "@/types.ts";
import api from "@/_helpers/api.ts";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { handleVirtualScrollData } from "@coronet/cui";
import { parseOpenSearchQuery } from "@/_helpers/pagination";
import { downloadFile } from "@/_helpers/download";
import { applyQuickFixAction, type QuickFixPayload } from "@/_helpers/ticket-helpers.ts";
import type { SubscriptionModule } from "@/constants/workplaces.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type {
  Ticket,
  TicketActionWithSeverity,
  TicketDetails,
  TicketMetadata,
  TicketSection,
} from "@/_store/tickets/tickets.module.ts";
import { StoreModule } from "@/constants/store.ts";
import { addGenericMetadataToRequest, handleTicketMetadata } from "@/_store/tickets/adapters.ts";
import {
  convertMspTicketFiltersForBackend,
  handleMspTicketBulkActionSnackbar,
  handleTicketActionSnackbar,
} from "@/_store/msp/tickets/adapters.ts";
import type {
  AbnormalDataManipulationFile,
  AbnormalDataManipulationFolder,
  DeviceDlpUnifiedFindingStructureItem,
  EdrDetectionAffectedDevice,
  FileFinding,
} from "@/_store/tickets/ticket.module.ts";
import cloneDeep from "lodash/cloneDeep";
import { AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { firstPageQueryEntityActivityLogs } from "@/constants/general.ts";
import { createAbortController } from "@coronet/cui";

export type MspTicketBulkActionPayload = {
  selection: MspTicket[];
  action: TicketAction;
  closeTicket?: boolean;
  metadata?: TicketMetadata;
};

export type MspTicket = Omit<Ticket, "socStatus"> & {
  workspaceId: string;
  workspaceName: string;
};

export type MspTicketDetails = Omit<TicketDetails, "socStatus"> & {
  workspaceId: string;
  workspaceName: string;
};

export interface MspTicketsStoreState {
  allTickets: number;
  pages: number;
  pagination: Pagination;
  tickets: MspTicket[];
  sorting: {
    sortBy: string;
    sortDesc: boolean;
  };
  showSkeletonLoader: boolean;
  loading: boolean;
  ticketDetailsLoading: boolean;
  activityLogsLoading: boolean;
  ticketDetails: MspTicketDetails;
  selectedEventId: string;
  fullDetails: {
    fileFindings: FileFinding[];
    fileFindingsLoading: boolean;
    fileFindingsTotal: number;
    folders: AbnormalDataManipulationFolder[];
    foldersLoaded: boolean;
    foldersTotal: number;
    edrAffectedDevices: EdrDetectionAffectedDevice[];
    edrAffectedDevicesLoading: boolean;
    edrAffectedDevicesTotal: number;
  };
  cursor: OpenSearchCursor;
}

const defaultTicketsState = {
  allTickets: 0,
  pages: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  tickets: [],
  sorting: {
    sortBy: "lastSeen",
    sortDesc: true,
  },
  showSkeletonLoader: true,
  loading: true,
  ticketDetailsLoading: false,
  activityLogsLoading: false,
  selectedEventId: "",
  ticketDetails: {
    activityLogs: [],
    activityLogsTotal: 0,
    processed: false,
    eventId: "",
    actions: [],
    potentiallyProtectableUsers: [],
    startTime: 0,
    endTime: 0,
    creationTime: 0,
    sections: {} as TicketSection,
    noProtectedUsersAtCreation: false,
    socModuleEnabled: false,
    sensitiveDataRestricted: false,
    lastLoggedInUsers: [],
    eventType: "" as TicketType,
    eventTrigger: "" as TicketTrigger,
    moduleName: "" as SubscriptionModule,
    workspaceId: "",
    workspaceName: "",
  } as MspTicketDetails,
  fullDetails: {
    fileFindings: [] as FileFinding[],
    fileFindingsLoading: false,
    fileFindingsTotal: 0,
    folders: [],
    foldersLoaded: false,
    foldersTotal: 0,
    edrAffectedDevices: [],
    edrAffectedDevicesLoading: false,
    edrAffectedDevicesTotal: 0,
  },
  cursor: {
    nextPage: firstPageQueryEntityActivityLogs,
  },
};

// Module-level abort controller for in-flight MSP tickets list request cancellation.
// This is intentionally outside the store so it survives across store resets.
let mspTicketsAbortController: AbortController;

export const useMspTicketsStore = defineStore(StoreModule.MSP_TICKETS, {
  state: (): MspTicketsStoreState => cloneDeep({ ...defaultTicketsState }),
  actions: {
    resetState() {
      this.$reset();
    },
    setPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
    resetPagination() {
      this.pagination = {
        page: 0,
        pageSize: 25,
      };
    },
    resetActivityLogs() {
      this.resetCursor();
      this.ticketDetails.activityLogs = [];
      this.ticketDetails.activityLogsTotal = 0;
    },
    async getMspTickets(showSkeletonLoader: boolean = true) {
      mspTicketsAbortController = createAbortController(mspTicketsAbortController);
      const { signal } = mspTicketsAbortController;

      this.showSkeletonLoader = showSkeletonLoader;
      this.loading = true;
      const filtersStore = useFiltersStore();

      const ticketsFilters = {
        ...convertMspTicketFiltersForBackend({ ...filtersStore.filters.mspTickets }),
      };
      const request = {
        ...api.getMspTickets(),
        params: {
          ...ticketsFilters,
          ...this.pagination,
          sort: "lastEventTime,desc",
        },
        signal,
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.tickets = data.items;
        this.allTickets = data.total;
      } catch (error) {
        if (error && (error as AxiosError).code === AxiosError.ERR_CANCELED) return;
        console.error(error);
      } finally {
        if (!signal.aborted) {
          this.showSkeletonLoader = false;
          this.loading = false;
        }
      }
    },
    async applyPaginationChange(payload: Pagination) {
      this.setPagination(payload);
      await this.getMspTickets();
    },
    async getMspTicketDetails({ eventId, workspaceId }: { eventId: string; workspaceId: string }) {
      this.ticketDetailsLoading = true;
      this.selectedEventId = eventId;
      const eventInfoRequest = {
        ...api.mspTicket(eventId),
        params: {
          workspaceId,
        },
      };

      try {
        const { data } = await axiosInstance.request(eventInfoRequest);

        this.ticketDetails = {
          ...data,
          // Activity logs are fetched via a separate endpoint and must not be overwritten here
          activityLogs: this.ticketDetails.activityLogs,
          activityLogsTotal: this.ticketDetails.activityLogsTotal,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.ticketDetailsLoading = false;
      }
    },
    async executeTicketAction(
      payload: DialogDataConfig<
        MspTicketDetails & {
          name?: string;
          closeTicket?: boolean;
          scope?: AllowBlockScope;
        },
        TicketAction
      >
    ): Promise<void> {
      // Toggle value from AllowBlockEmailModalForm
      const isGlobalScope = payload.item.scope === AllowBlockScope.GLOBAL;
      const request: AxiosRequestConfig = isGlobalScope
        ? {
            ...api.mspTicketBulkAction(),
            data: {
              ticketIds: [payload.item.eventId],
              action: payload.action,
              service: payload.item?.sections?.serviceName ?? "",
              closeTicket: payload.item.closeTicket,
            },
          }
        : {
            ...api.mspTicketAction(payload.item.eventId),
            data: {
              action: payload.action,
              service: payload.item?.sections?.serviceName ?? "",
              closeTicket: payload.item.closeTicket,
            },
            params: {
              workspaceId: payload.item.workspaceId,
            },
          };

      handleTicketMetadata(request, payload);
      return await axiosInstance.request(request);
    },
    async applyMspTicketAction(
      payload: DialogDataConfig<
        MspTicketDetails & { name?: string; closeTicket?: boolean },
        TicketAction
      >
    ) {
      try {
        await this.executeTicketAction(payload);
        await this.getMspTickets();
        handleTicketActionSnackbar(payload);
      } catch (err) {
        console.error(err);
      }
    },
    async applyMspTicketDownloadEmailAction(
      payload: DialogDataConfig<
        MspTicketDetails & { name?: string; closeTicket?: boolean },
        TicketAction
      >
    ) {
      const request = {
        ...api.mspTicketAction(payload.item.eventId),
        data: {
          action: payload.action,
        },
        params: {
          workspaceId: payload.item.workspaceId,
        },
      };

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = headers["x-coro-attachment-filename"];
        downloadFile(blob, fileName);

        await this.getMspTickets();

        const snackbarStore = useSnackbarStore();

        snackbarStore.addGenericSuccess(
          i18n.global.t(`snackbar.messages.event.${payload.action}`, {
            fileName,
          })
        );
      } catch (err) {
        console.error(err);
      }
    },
    async getMspTicketActivityLogs({
      eventId,
      workspaceId,
    }: {
      eventId: string;
      workspaceId?: string;
    }) {
      this.activityLogsLoading = true;

      try {
        const request = {
          ...api.getMspTicketActivityLogs(eventId),
          params: {
            ...parseOpenSearchQuery(this.cursor.nextPage!),
            workspaceId,
          },
        };

        const { data } = await axiosInstance.request(request);

        this.cursor = data.cursor;

        this.ticketDetails = {
          ...this.ticketDetails,
          activityLogs: handleVirtualScrollData(this.ticketDetails.activityLogs, data.items, "id"),
          activityLogsTotal: data.total,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.activityLogsLoading = false;
      }
    },
    async undoActivityLog(id: string) {
      this.activityLogsLoading = true;

      try {
        const request = {
          ...api.activityLogsUndo(id),
          params: {
            workspaceId: this.ticketDetails.workspaceId,
          },
          headers: {
            Workspace: this.ticketDetails.workspaceId,
          },
        };

        await axiosInstance.request(request);
        this.resetCursor();
        await this.updateMspTicketActivityLogs();
        await this.getMspTickets();
      } catch (error) {
        console.error(error);
      }

      this.activityLogsLoading = false;
    },
    async updateMspTicketActivityLogs() {
      this.activityLogsLoading = true;
      try {
        const request = {
          ...api.getMspTicketActivityLogs(this.ticketDetails.eventId),
          params: {
            ...parseOpenSearchQuery(this.cursor.nextPage!),
            workspaceId: this.ticketDetails.workspaceId,
          },
        };
        const { data } = await axiosInstance.request(request);
        this.ticketDetails.activityLogs = data.items;
      } catch (e) {
        console.error(e);
      }

      this.activityLogsLoading = false;
    },
    async getFileFindings({
      eventId,
      page = 0,
      virtualScroll,
    }: {
      page: number;
      virtualScroll?: boolean;
      eventId: string;
    }) {
      this.fullDetails.fileFindingsLoading = true;
      try {
        const request = {
          ...api.mspTicketFindings(eventId),
          params: {
            page,
            pageSize: 10,
            workspaceId: this.ticketDetails.workspaceId,
          },
        };
        const { data } = await axiosInstance.request<Paged<FileFinding>>(request);
        for (const item of data.items) {
          item.id = item.filePath + item.fileName;
        }
        this.fullDetails.fileFindings = virtualScroll
          ? handleVirtualScrollData(this.fullDetails.fileFindings, data.items, "id")
          : data.items;
        this.fullDetails.fileFindingsTotal = data.total;
      } catch (error) {
        console.error(error);
      } finally {
        this.fullDetails.fileFindingsLoading = false;
      }
    },
    async getUnifiedFileFindingsStructure(ticketId: string) {
      const request = {
        ...api.mspDlpFindingsStructure(ticketId),
        params: {
          workspaceId: this.ticketDetails.workspaceId,
        },
      };

      return axiosInstance.request<DeviceDlpUnifiedFindingStructureItem[]>(request);
    },
    async getUnifiedFileFindingsData(eventId: string, ticketTrigger: TicketTrigger, page: number) {
      const request = {
        ...api.mspDlpUnifiedFindings({
          eventId,
          page,
          pageSize: 10,
          eventTrigger: ticketTrigger,
          workspaceId: this.ticketDetails.workspaceId,
        }),
      };
      return axiosInstance.request<Paged<FileFinding>>(request);
    },
    //@TODO Remove once BE will send a proper findings
    async getSharedFileNames(ticketId: string) {
      const request = {
        ...api.mspDlpFileNames(ticketId),
        params: {
          workspaceId: this.ticketDetails.workspaceId,
        },
      };
      return axiosInstance.request<string[]>(request);
    },
    async getMassDeleteDownloadFolders({
      eventId,
      page = 0,
      virtualScroll,
    }: {
      eventId: string;
      page: number;
      virtualScroll?: boolean;
    }) {
      try {
        const request = {
          ...api.mspTicketAbnormalDataManipulationFolders(eventId),
          params: {
            page,
            pageSize: 10,
            workspaceId: this.ticketDetails.workspaceId,
          },
        };
        const { data } = await axiosInstance.request(request);
        this.fullDetails.folders = virtualScroll
          ? handleVirtualScrollData(this.fullDetails.folders, data.items, "id")
          : data.items;
        this.fullDetails.foldersTotal = data.total;
      } catch (error) {
        console.error(error);
      }
      this.fullDetails.foldersLoaded = true;
    },
    async getMassDeleteDownloadFiles({
      eventId,
      page = 0,
      folderId,
    }: {
      eventId: string;
      page: number;
      folderId: string;
    }): Promise<Paged<AbnormalDataManipulationFile>> {
      try {
        const request = {
          ...api.mspTicketAbnormalDataManipulationFiles({
            eventId,
            folderId,
          }),
          params: {
            page,
            pageSize: 10,
            workspaceId: this.ticketDetails.workspaceId,
          },
        };
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return { items: [], total: 0 };
      }
    },
    async getEdrAffectedDevices({
      eventId,
      page = 0,
      virtualScroll,
    }: {
      eventId: string;
      page: number;
      virtualScroll?: boolean;
    }) {
      this.fullDetails.edrAffectedDevicesLoading = true;

      try {
        const request = {
          ...api.mspTicketEdrAffectedDevices(eventId),
          params: { page, pageSize: 10, workspaceId: this.ticketDetails.workspaceId },
        };

        const { data } = await axiosInstance.request(request);
        for (const item of data.items) {
          item.id =
            item.filePath + item.processName + item.processCreationTime + item.executionTime;
        }
        this.fullDetails.edrAffectedDevices = virtualScroll
          ? handleVirtualScrollData(this.fullDetails.edrAffectedDevices, data.items, "id")
          : data.items;
        this.fullDetails.edrAffectedDevicesTotal = data.total;
      } catch (error) {
        console.error(error);
      }

      this.fullDetails.edrAffectedDevicesLoading = false;
    },
    async getTicketRecipients(eventId: string, isDeviceEvent: boolean, workspaceId: string) {
      const params: { protectedOnly?: boolean; workspaceId: string } = { workspaceId };
      if (isDeviceEvent) {
        params.protectedOnly = false;
      }
      const request = {
        ...api.getMspTicketRecipients(eventId),
        params,
      };
      try {
        const { data } = await axiosInstance.request(request);

        return data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async onContactUser(
      payload: DialogDataConfig<{
        includeEventDetails: boolean;
        contactMessage: string;
        recipients: string[];
        id: string;
        workspaceName: string;
      }>
    ) {
      const { includeEventDetails, contactMessage, recipients, id, workspaceName } = payload.item;
      const request = {
        ...api.contactUser,
        params: {
          id: id,
          workspaceId: this.ticketDetails.workspaceId,
        },
        data: {
          contactMessage: contactMessage,
          includeEventDetails: includeEventDetails,
          recipients: recipients,
          workspaceDisplayName: workspaceName,
        },
        headers: {
          Workspace: this.ticketDetails.workspaceId,
        },
      };

      try {
        await axiosInstance.request(request);

        useSnackbarStore().addGenericSuccess(i18n.global.t("snackbar.messages.users.contactUser"));
        this.resetCursor();
        await this.updateMspTicketActivityLogs();
      } catch (error) {
        console.error(error);
      }
    },
    async applyMspTicketBulkAction(payload: MspTicketBulkActionPayload) {
      const { action, closeTicket, selection } = payload;
      try {
        const request = {
          ...api.mspTicketBulkAction(),
          data: {
            ticketIds: selection.map((v) => v.eventId),
            action,
            closeTicket,
          },
        };
        if (payload.metadata) {
          addGenericMetadataToRequest(request, payload.metadata);
        }
        await axiosInstance.request(request);
        handleMspTicketBulkActionSnackbar({ action, closeTicket, selection });
        await this.getMspTickets();
      } catch (e) {
        console.error(e);
      }
    },

    getBulkActions(
      items: MspTicket[]
    ): Promise<AxiosResponse<{ items: TicketActionWithSeverity[] }>> {
      const request = {
        ...api.availableMspTicketBulkActions,
        data: {
          itemIds: items.map((v) => v.eventId),
        },
      };

      return axiosInstance.request(request);
    },
    resetCursor() {
      this.cursor = {
        nextPage: firstPageQueryEntityActivityLogs,
      };
    },
    async applyQuickFixAction(payload: DialogDataConfig<QuickFixPayload<MspTicketDetails>>) {
      await applyQuickFixAction(payload, this.getQuickFixCallback);
      await this.getMspTickets();
    },
    getQuickFixCallback(
      action: TicketAction,
      payload: DialogDataConfig<QuickFixPayload<MspTicketDetails>>
    ) {
      switch (action) {
        case TicketAction.CONTACT_USER:
          const contactUserPayload: DialogDataConfig<{
            includeEventDetails: boolean;
            contactMessage: string;
            recipients: string[];
            id: string;
            workspaceName: string;
          }> = {
            ...payload,
            item: {
              ...payload.item.metadata[TicketAction.CONTACT_USER]!,
              id: payload.item.ticket.eventId,
              workspaceName: payload.item.ticket.workspaceName,
            },
            selection: [],
          };
          return this.onContactUser(contactUserPayload);
        default:
          const actionToExecute =
            action === TicketAction.DISCARD_EMAIL_WITH_OPTIONS
              ? payload.item.metadata[TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?.action!
              : action;

          const executeTicketActionPayload = {
            ...payload,
            item: {
              ...payload.item.ticket,
            },
            action: actionToExecute,
            metadata: {
              ...payload.item.metadata,
              action,
            },
            selection: [],
          };
          return this.executeTicketAction(executeTicketActionPayload);
      }
    },
  },
});
